import * as React from "react";
import {
  Input,
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Badge,
} from "@repo/ui";
import { Search, X, Check, ChevronDown } from "lucide-react";
import { formatToApiDate, parseDateString, cn } from "@repo/utils";
import { DateRangeFilter } from "@/components/forms";
import { getAuthUsers } from "@/lib/api/auth";
import type { ActivityFilters as ActivityFiltersType } from "@/lib/types/activity";

interface User {
  id: string;
  initials: string;
  full_name: string | null;
  role: string;
}

interface ActivityFiltersProps {
  filters: ActivityFiltersType;
  onFilterChange: (filters: Partial<ActivityFiltersType>) => void;
  onReset: () => void;
}

export function ActivityFilters({
  filters,
  onFilterChange,
  onReset,
}: ActivityFiltersProps) {
  const [users, setUsers] = React.useState<User[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = React.useState(true);
  const [usersOpen, setUsersOpen] = React.useState(false);

  const selectedUserIds = filters.userIds ? filters.userIds.split(",") : [];

  React.useEffect(() => {
    const fetchUsers = async () => {
      try {
        const data = await getAuthUsers({ pageSize: 100 });
        setUsers(data.data?.users || data.users || []);
      } catch (error) {
        console.error("Failed to fetch users:", error);
      } finally {
        setIsLoadingUsers(false);
      }
    };
    void fetchUsers();
  }, []);

  const toggleUser = (userId: string) => {
    const next = selectedUserIds.includes(userId)
      ? selectedUserIds.filter((id) => id !== userId)
      : [...selectedUserIds, userId];
    onFilterChange({ userIds: next.length > 0 ? next.join(",") : null });
  };

  const dateRange = {
    from: filters.startDate ? parseDateString(filters.startDate) : undefined,
    to: filters.endDate ? parseDateString(filters.endDate) : undefined,
  };

  const handleReset = () => {
    onReset();
  };

  const hasActiveFilters =
    filters.startDate ||
    filters.endDate ||
    (filters.transactionType && filters.transactionType !== "all") ||
    filters.search ||
    (filters.userIds && filters.userIds.length > 0);

  const userLabel =
    selectedUserIds.length === 0
      ? "Users"
      : selectedUserIds.length === 1
        ? (users.find((u) => u.id === selectedUserIds[0])?.full_name ??
          users.find((u) => u.id === selectedUserIds[0])?.initials ??
          "1 user")
        : `${selectedUserIds.length} users`;

  return (
    <div className="flex flex-wrap items-center gap-2">
      {/* Search */}
      <div className="relative w-[440px]">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search by item name, NDC, or reference number..."
          value={filters.search}
          onChange={(e) => onFilterChange({ search: e.target.value })}
          className="pl-10 h-9"
          data-testid="activity-search-input"
        />
      </div>

      {/* Date range */}
      <DateRangeFilter
        value={dateRange}
        onChange={({ from, to }) => {
          onFilterChange({
            startDate: from ? formatToApiDate(from) : null,
            endDate: to ? formatToApiDate(to) : null,
          });
        }}
      />

      {/* Transaction type */}
      <Select
        value={filters.transactionType || "all"}
        onValueChange={(value) =>
          onFilterChange({
            transactionType:
              value === "all"
                ? null
                : (value as "receipt" | "dispense" | "adjustment"),
          })
        }
      >
        <SelectTrigger className="h-9 w-[150px] text-sm font-normal">
          <SelectValue placeholder="All Types" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Types</SelectItem>
          <SelectItem value="receipt">Receipts</SelectItem>
          <SelectItem value="dispense">Dispenses</SelectItem>
          <SelectItem value="adjustment">Adjustments</SelectItem>
        </SelectContent>
      </Select>

      {/* Users popover */}
      <Popover open={usersOpen} onOpenChange={setUsersOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={cn(
              "h-9 gap-1.5 font-normal",
              selectedUserIds.length > 0 && "border-primary/50 text-primary"
            )}
            disabled={isLoadingUsers}
          >
            {userLabel}
            {selectedUserIds.length > 0 && (
              <Badge
                variant="secondary"
                className="h-4 px-1 text-[10px] leading-none"
              >
                {selectedUserIds.length}
              </Badge>
            )}
            <ChevronDown className="h-3.5 w-3.5 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[200px] p-0" align="start">
          <Command>
            <CommandInput placeholder="Search users..." />
            <CommandList>
              <CommandEmpty>No users found.</CommandEmpty>
              <CommandGroup>
                {users.map((user) => (
                  <CommandItem
                    key={user.id}
                    onSelect={() => toggleUser(user.id)}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        selectedUserIds.includes(user.id)
                          ? "opacity-100"
                          : "opacity-0"
                      )}
                    />
                    {user.full_name || user.initials}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Clear all */}
      {hasActiveFilters && (
        <Button
          variant="ghost"
          size="sm"
          onClick={handleReset}
          className="h-9 px-2 text-muted-foreground hover:text-foreground"
        >
          Clear all
          <X className="ml-1 h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  );
}
